import React, { useState, useEffect } from 'react';
import { Box, IconButton, useTheme, Fade, Dialog, DialogContent } from '@mui/material';
import { ChevronLeft, ChevronRight, Circle, Close as CloseIcon } from '@mui/icons-material';

type Screenshot = { src: string; alt: string };

const defaultScreenshots: Screenshot[] = [
  { src: '/screenshots/commission-report.png', alt: 'Commission Report' },
  { src: '/screenshots/sales-listing-report.png', alt: 'Sales / Listing Report' },
  { src: '/screenshots/add-new-property.png', alt: 'Property Onboarding' },
  { src: '/screenshots/workflow-progress.png', alt: 'Workflow Progress Tracking' },
  { src: '/screenshots/create-open-schedule.png', alt: 'Open House Scheduling' },
  { src: '/screenshots/calendar-assistant.png', alt: 'Smart Calendar Assistant' },
  { src: '/screenshots/properties-list.png', alt: 'Property Portfolio' },
  { src: '/screenshots/market-campaign.png', alt: 'Market Campaign Management' },
  { src: '/screenshots/market-campaign-sending.png', alt: 'Market Campaign Sending' },
  { src: '/screenshots/contractor-dashboard-home.png', alt: 'Contractor Dashboard Overview' },
  { src: '/screenshots/job-details.png', alt: 'Detailed Job View' },
  { src: '/screenshots/contractor-availability.png', alt: 'Availability Management' },
  { src: '/screenshots/add-availability-slot.png', alt: 'Time Slot Management' },
];

interface ScreenshotCarouselProps {
  screenshots?: Screenshot[];
}

export const ScreenshotCarousel = ({ screenshots }: ScreenshotCarouselProps) => {
  const slides = screenshots && screenshots.length > 0 ? screenshots : defaultScreenshots;
  const [activeStep, setActiveStep] = useState(0);
  const [fullSizeOpen, setFullSizeOpen] = useState(false);
  const [fullSizeImage, setFullSizeImage] = useState<Screenshot | null>(null);
  const [fullSizeIndex, setFullSizeIndex] = useState(0);
  const theme = useTheme();
  const maxSteps = slides.length || 1;

  const handleNext = () => {
    setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => (prevStep - 1 + maxSteps) % maxSteps);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const handleImageClick = (screenshot: Screenshot, index?: number) => {
    const imageIndex = index !== undefined ? index : slides.findIndex(s => s.src === screenshot.src);
    setFullSizeImage(screenshot);
    setFullSizeIndex(imageIndex >= 0 ? imageIndex : activeStep);
    setFullSizeOpen(true);
  };

  const handleCloseFullSize = () => {
    setFullSizeOpen(false);
    setFullSizeImage(null);
  };

  const handleFullSizeNext = () => {
    const nextIndex = (fullSizeIndex + 1) % maxSteps;
    setFullSizeIndex(nextIndex);
    setFullSizeImage(slides[nextIndex]);
  };

  const handleFullSizePrev = () => {
    const prevIndex = (fullSizeIndex - 1 + maxSteps) % maxSteps;
    setFullSizeIndex(prevIndex);
    setFullSizeImage(slides[prevIndex]);
  };

  // Minimum swipe distance (in pixels) to trigger navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && maxSteps > 1) {
      handleFullSizeNext();
    }
    if (isRightSwipe && maxSteps > 1) {
      handleFullSizePrev();
    }
  };

  // Handle keyboard navigation in full-size mode
  useEffect(() => {
    if (!fullSizeOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handleFullSizePrev();
      } else if (e.key === 'ArrowRight') {
        handleFullSizeNext();
      } else if (e.key === 'Escape') {
        handleCloseFullSize();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullSizeOpen, fullSizeIndex, maxSteps]);

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: { xs: 300, md: 500, lg: 600 },
      bgcolor: 'grey.100',
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid',
      borderColor: 'divider'
    }}>
      {slides.map((step, index) => (
        <Fade in={activeStep === index} timeout={500} key={step.src}>
          <Box
            component="img"
            onClick={() => handleImageClick(step, index)}
            sx={{
              display: activeStep === index ? 'block' : 'none',
              width: '100%',
              height: '100%',
              objectFit: 'contain', // or cover, depending on preference. Contain is safer for screenshots.
              bgcolor: 'background.default',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              '&:hover': {
                opacity: 0.9
              }
            }}
            src={step.src}
            alt={step.alt}
          />
        </Fade>
      ))}

      {/* Navigation Buttons */}
      <IconButton
        onClick={handleBack}
        sx={{
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'rgba(255,255,255,0.8)',
          '&:hover': { bgcolor: 'white' }
        }}
      >
        <ChevronLeft />
      </IconButton>

      <IconButton
        onClick={handleNext}
        sx={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'rgba(255,255,255,0.8)',
          '&:hover': { bgcolor: 'white' }
        }}
      >
        <ChevronRight />
      </IconButton>

      {/* Indicators */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 16, 
        left: '50%', 
        transform: 'translateX(-50%)', 
        display: 'flex', 
        gap: 1,
        bgcolor: 'rgba(0,0,0,0.3)',
        px: 2,
        py: 0.5,
        borderRadius: 4
      }}>
        {slides.map((_, index) => (
          <Circle
            key={index}
            sx={{
              fontSize: 10,
              cursor: 'pointer',
              color: activeStep === index ? 'white' : 'rgba(255,255,255,0.5)',
            }}
            onClick={() => handleStepChange(index)}
          />
        ))}
      </Box>

      {/* Full-size Image Modal */}
      <Dialog
        open={fullSizeOpen}
        onClose={handleCloseFullSize}
        maxWidth={false}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100%',
            height: '100%',
            m: 0,
            bgcolor: 'transparent',
            boxShadow: 'none',
            [theme.breakpoints.up('md')]: {
              maxWidth: '95vw',
              maxHeight: '95vh',
              width: 'auto',
              height: 'auto',
              m: 2
            }
          }
        }}
      >
        <DialogContent 
          sx={{ p: 0, position: 'relative', bgcolor: 'rgba(0,0,0,0.9)', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseFullSize}
            sx={{
              position: 'absolute',
              top: { xs: 8, md: 8 },
              right: { xs: 8, md: 8 },
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.6)',
              zIndex: 2,
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)'
              },
              '&:active': {
                bgcolor: 'rgba(0,0,0,0.9)'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
          </IconButton>

          {/* Previous Button */}
          {maxSteps > 1 && (
            <IconButton
              onClick={handleFullSizePrev}
              sx={{
                position: 'absolute',
                left: { xs: 8, md: 16 },
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.6)',
                zIndex: 2,
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.8)'
                },
                '&:active': {
                  bgcolor: 'rgba(0,0,0,0.9)'
                }
              }}
            >
              <ChevronLeft sx={{ fontSize: { xs: 28, md: 32 } }} />
            </IconButton>
          )}

          {/* Next Button */}
          {maxSteps > 1 && (
            <IconButton
              onClick={handleFullSizeNext}
              sx={{
                position: 'absolute',
                right: { xs: 8, md: 16 },
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.6)',
                zIndex: 2,
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.8)'
                },
                '&:active': {
                  bgcolor: 'rgba(0,0,0,0.9)'
                }
              }}
            >
              <ChevronRight sx={{ fontSize: { xs: 28, md: 32 } }} />
            </IconButton>
          )}

          {/* Image Counter */}
          {maxSteps > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 24, md: 16 },
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.6)',
                px: { xs: 2, md: 2 },
                py: { xs: 1, md: 0.5 },
                borderRadius: 2,
                zIndex: 2,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 500
              }}
            >
              {fullSizeIndex + 1} / {maxSteps}
            </Box>
          )}

          {/* Full-size Image */}
          {fullSizeImage && (
            <Box
              component="img"
              src={fullSizeImage.src}
              alt={fullSizeImage.alt}
              sx={{
                maxWidth: '100%',
                maxHeight: { xs: '100vh', md: '95vh' },
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                mx: 'auto',
                py: { xs: 4, md: 2 }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScreenshotCarousel;

